/*
 * gg-header-price.js — Gummy Gardens, 2026-09-20
 *
 * Shows the real price under the product title: the list price struck through, the discounted total, and the
 * dollars saved, matching whichever purchase option is selected.
 *
 * Needed because the one-time multipack saving is delivered by Shopify AUTOMATIC discounts (5% on 2-packs, 10% on
 * 3-packs, one-time orders only). Automatic discounts apply in the cart and are invisible on a product page, so
 * without this the title price contradicts what the shopper is actually charged.
 *
 * SCOPE — deliberately narrow. This ONLY writes to the theme's own price element. It never writes anything inside
 * Recharge's subscription widget. An earlier version did, and it broke the widget: Recharge renders with lit, which
 * tracks its bindings with markers inside its nodes, so writing text or appending children corrupted them and
 * selecting a purchase option stopped working on exactly the packs that were touched. The widget is READ here (to
 * learn which option is selected) and never modified. Reading cannot break it.
 *
 * Rates must match Shopify's automatic discounts and the Recharge plans. If either changes, change them here.
 *
 * 2026-10-29 PACK-PRICE CUTOVER (founder-approved): works in BOTH states, so it can be deployed before the switch.
 *  - New state (variant has compare_at_price > price): list = compare-at ($80 / $120), one-time = the variant price
 *    as-is ($76 / $108; no -5% / -10% painting), subscribe = the selling-plan price Shopify reports for the variant
 *    (Recharge fixed $8 / $12 off -> $68 / $96), saving shown against the compare-at ("save $12" / "save $24").
 *  - Old state (no compare-at): exactly today's maths (list = variant price, ONETIME / SUB rates below).
 *  Variant data comes from /products/<handle>.js (price, compare_at_price, selling_plan_allocations); while that is
 *  loading, or if it fails, the DOM + rate fallback below is used. The rate tables are only that fallback.
 */
(function () {
  'use strict';

  var ONETIME = { '2PK': 0.05, '3PK': 0.10 };
  var SUB     = { '1PK': 0.10, '2PK': 0.15, '3PK': 0.20 };
  var TAG = 'gg-hdr-saving';
  var DETAIL = 'gg-onetime-detail';
  var BADGE = 'gg-onetime-badge';
  var SUBBADGE = 'gg-sub-badge';
  var SHIPBADGE = 'gg-onetime-ship';   // founder 2026-09-30: every order ships free — tag the one-time option too
  var ONEPRICE = 'gg-onetime-price';
  var GREEN = 'rgb(15,134,106)';   // Recharge's own badge green, so the savings read as one system

  function money(n) {
    var t = n.toFixed(2);
    return '$' + (t.slice(-3) === '.00' ? t.slice(0, -3) : t);
  }
  function round2(n) { return Math.round(n * 100) / 100; }

  function num(el) {
    var m = ((el || {}).textContent || '').replace(/,/g, '').match(/\$\s*([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  }

  /* -- Variant data (both states) -- */
  var PRODUCT = null, loading = false;
  function loadProduct() {
    if (PRODUCT || loading || typeof fetch !== 'function') return;
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return;
    loading = true;
    fetch('/products/' + m[1] + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { PRODUCT = p; loading = false; schedule(); })
      .catch(function () { loading = false; });
  }
  function selectedVariant() {
    if (!PRODUCT || !PRODUCT.variants) return null;
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    var id = input && input.value;
    if (!id) { var q = location.search.match(/[?&]variant=(\d+)/); id = q && q[1]; }
    for (var i = 0; i < PRODUCT.variants.length; i++) if (String(PRODUCT.variants[i].id) === String(id)) return PRODUCT.variants[i];
    return null;
  }
  /* The selling-plan price for this variant (cents in products.js). Prefers the plan selected in the form. */
  function sellingPlanPrice(v) {
    var a = (v && v.selling_plan_allocations) || [];
    if (!a.length) return null;
    var sel = document.querySelector('form[action*="/cart/add"] [name="selling_plan"]');
    var want = sel && sel.value;
    for (var i = 0; i < a.length; i++) if (want && String(a[i].selling_plan_id) === String(want)) return a[i].price / 100;
    return a[0].price / 100;
  }

  /**
   * Prices for the selected pack, in either state.
   * Returns { list, oneTime, sub, compareMode, el, saleEl }; el is the theme's VISIBLE list-price element.
   */
  function prices(p) {
    var root = document.querySelector('.product__info-container') || document;
    var priceBox = root.querySelector('.price');
    var onSaleDom = !!(priceBox && priceBox.classList.contains('price--on-sale'));
    var v = selectedVariant();
    var compareMode, list, oneTime, sub = null;
    if (v) {
      compareMode = v.compare_at_price != null && v.compare_at_price > v.price;
      list = (compareMode ? v.compare_at_price : v.price) / 100;
      oneTime = compareMode ? v.price / 100 : round2(list * (1 - (ONETIME[p] || 0)));
      sub = sellingPlanPrice(v);
    } else if (onSaleDom) {
      compareMode = true;
      list = num(priceBox.querySelector('.price__sale .price-item--regular'));
      oneTime = num(priceBox.querySelector('.price__sale .price-item--sale'));
    } else {
      compareMode = false;
      list = num(root.querySelector('.price-item--regular'));
      oneTime = list ? round2(list * (1 - (ONETIME[p] || 0))) : null;
    }
    // Fallback only (no selling-plan data): correct in both states because list is the list price.
    if (sub == null && list && SUB[p]) sub = round2(list * (1 - SUB[p]));
    var el = compareMode
      ? (priceBox && priceBox.querySelector('.price__sale .price-item--regular'))
      : root.querySelector('.price-item--regular');
    return { list: list, oneTime: oneTime, sub: sub, compareMode: compareMode, el: el,
             saleEl: priceBox && priceBox.querySelector('.price__sale .price-item--sale') };
  }
  function pct(save, list) { return Math.round(save / list * 100); }

  function pack() {
    var c = document.querySelector('.product-form__input input[type="radio"]:checked[value="1PK"], .product-form__input input[type="radio"]:checked[value="2PK"], .product-form__input input[type="radio"]:checked[value="3PK"]');
    return c ? c.value : null;
  }

  /** READ ONLY. Walks Recharge's open shadow roots to see which option is selected. Never writes. */
  function subscribeSelected() {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return true;            // widget absent or not ready: assume the default
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var blk = node.querySelector('.rc-purchase-option__subscription');
        if (blk) {
          var radio = blk.querySelector('input[type="radio"]');
          if (radio) return !!radio.checked;
        }
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return true;
  }


  /* Founder 2026-09-30 (mobile): badges must move with their option and never float over the sticky header.
     They are mounted in the widget's light-DOM parent (outside Recharge's shadow DOM, so the widget itself is still
     never written to) and positioned relative to it, so they scroll with the page like part of the form. */
  function anchorBox() {
    var host = document.querySelector('recharge-subscription-widget');
    var a = host && host.parentElement;
    if (!a) return null;
    if (getComputedStyle(a).position === 'static') a.style.position = 'relative';
    return a;
  }
  function relLeft(x, a) { return Math.round(x - a.getBoundingClientRect().left); }
  function relTop(y, a) { return Math.round(y - a.getBoundingClientRect().top); }

  /** READ ONLY. Find an element inside Recharge's nested shadow roots. Never writes. */
  function findInWidget(selector) {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return null;
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var hit = node.querySelector(selector);
        if (hit) return hit;
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return null;
  }

  /**
   * Float the saving badge over the one-time option, mirroring where Recharge puts its own.
   * It lives in the light DOM and is positioned from the option's measured rect, so the widget is never modified.
   * pointer-events is none: Recharge's own badge sits over its option and outside the <label>, which is why it
   * swallowed taps and made Subscribe unselectable. This one can never intercept a tap.
   */
  function paintBadge(id, selector, label) {
    var existing = document.getElementById(id);
    var box = label ? findInWidget(selector) : null;
    if (!box) { if (existing) existing.remove(); return; }
    // If Recharge already renders a badge on this option, sit exactly on top of it and match its width, so ours
    // covers theirs completely. Ours can be shorter than theirs ("Save $12 (15%)" vs "Save up to 15%"), so
    // matching the width matters — without it theirs peeks out from behind.
    var theirs = box.querySelector('.rc-purchase-option__badge');
    var r = (theirs ? theirs : box).getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }
    var coverWidth = theirs ? Math.ceil(r.width) : 0;
    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      (anchorBox() || document.body).appendChild(el);
    }
    el.textContent = label;
    // Founder 2026-09-30: dollars first ("Save $12 (15%)"), larger, and in the same flavour accent as the pack and
    // flavour buttons. --gg-accent / --gg-on-accent live on <html> (gg-flavor-theme.css) and change with the selected
    // flavour, so the badge recolours itself; brand purple when no flavour is set. Anchored on Recharge's badge
    // top-right corner (or -11px / 13px from the option's corner) and at least as wide, so theirs stays covered.
    el.setAttribute('style', [
      'position:absolute',
      'z-index:2',
      'pointer-events:none',
      'background:rgb(var(--gg-accent, 131, 122, 232))',
      'color:rgb(var(--gg-on-accent, 255, 255, 255))',
      'border:0',  // founder 10-12 (final): no border on discount tags
      'border-radius:6px',
      'padding:7px 10px',
      'font-family:Barlow,sans-serif',
      'font-size:14px',
      'font-weight:700',
      'line-height:14px',
      'letter-spacing:0.4px',
      'box-shadow:0 2px 6px rgba(0,26,48,.18)',
      'white-space:nowrap',
      coverWidth ? ('min-width:' + coverWidth + 'px') : '',
      'box-sizing:border-box',
      'text-align:center',
      'left:' + (anchorBox() ? relLeft(r.right - (theirs ? 0 : 13), anchorBox()) : Math.round(r.right + window.scrollX - (theirs ? 0 : 13))) + 'px',
      'top:' + (anchorBox() ? relTop(r.top - (theirs ? 4 : 13), anchorBox()) : Math.round(r.top + window.scrollY - (theirs ? 4 : 13))) + 'px',
      'transform:translateX(-100%) rotate(-4deg)',  // founder 10-12: tag shape 8, tilted sticker
      'transform-origin:100% 50%'
    ].join(';'));
  }


  /**
   * Cover Recharge's one-time price with the real one, so the option agrees with the price under the title.
   * Recharge prints the raw list price there ($120.00) because the one-time saving comes from a Shopify automatic
   * discount it knows nothing about. Painted over from the light DOM — the widget is still never written to.
   */
  function paintOneTimePrice(list, total) {
    var existing = document.getElementById(ONEPRICE);
    var box = (total != null) ? findInWidget('.rc-selection__onetime') : null;
    var theirPrice = box ? box.querySelector('.rc-price') : null;
    if (!theirPrice) { if (existing) existing.remove(); return; }

    var r = theirPrice.getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }

    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = ONEPRICE;
      (anchorBox() || document.body).appendChild(el);
    }
    el.innerHTML = '';
    var cs = getComputedStyle(theirPrice);
    // Match the surrounding surface so their figure is hidden rather than showing through.
    var bg = null;
    var walk = theirPrice.parentElement, guard = 0;
    while (walk && guard++ < 6) {
      if (walk.matches && walk.matches('.product__info-container')) break; // the panel's colour is its gradient, sampled below
      var c = getComputedStyle(walk).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { bg = c; break; }
      walk = walk.parentElement;
    }
    // founder 2026-10-12: the one-time card is transparent over the flavour panel, whose background is a vertical
    // gradient (flavour tint fading to white). Sample that gradient at the price's height so the overlay matches
    // the surface it sits on for every flavour.
    if (!bg) bg = panelColorAt(theirPrice) || 'rgb(255,255,255)';
    var strike = document.createElement('span');
    strike.setAttribute('style', 'text-decoration:line-through;opacity:.6;margin-right:6px');
    strike.textContent = money(list);
    var now = document.createElement('span');
    now.textContent = money(total);
    el.appendChild(strike);
    el.appendChild(now);
    el.setAttribute('style', [
      'position:absolute',
      'z-index:1',
      'pointer-events:none',
      'background:' + bg,
      'color:' + cs.color,
      'font-family:' + cs.fontFamily,
      'font-size:' + cs.fontSize,
      'font-weight:' + cs.fontWeight,
      'line-height:' + cs.lineHeight,
      'white-space:nowrap',
      'left:' + (anchorBox() ? relLeft(r.left, anchorBox()) : Math.round(r.left + window.scrollX)) + 'px',
      'top:' + (anchorBox() ? relTop(r.top, anchorBox()) : Math.round(r.top + window.scrollY)) + 'px',
      'min-width:' + Math.ceil(r.width) + 'px',
      'min-height:' + Math.ceil(r.height) + 'px'
    ].join(';'));
  }



  function panelColorAt(node) {
    try {
      var panel = (node.closest && node.closest('.product__info-container')) || document.querySelector('.product__info-container'); if (!panel) return null;
      var img = getComputedStyle(panel).backgroundImage || '';
      var m = img.match(/linear-gradient\((.*)\)$/); if (!m) return null;
      var stops = [], re = /rgba?\(([^)]+)\)\s*([\d.]+%)?/g, x;
      while ((x = re.exec(m[1]))) { var v = x[1].split(',').map(function (t) { return parseFloat(t); }); stops.push({ c: v, p: x[2] ? parseFloat(x[2]) / 100 : null }); }
      if (stops.length < 2) return null;
      stops.forEach(function (st, i) { if (st.p === null) st.p = i === 0 ? 0 : (i === stops.length - 1 ? 1 : null); });
      var pr = panel.getBoundingClientRect(), nr = node.getBoundingClientRect();
      var f = Math.min(1, Math.max(0, (nr.top + nr.height / 2 - pr.top) / pr.height));
      var a = stops[0], b = stops[stops.length - 1];
      for (var i = 0; i < stops.length - 1; i++) if (stops[i].p !== null && stops[i + 1].p !== null && f >= stops[i].p && f <= stops[i + 1].p) { a = stops[i]; b = stops[i + 1]; break; }
      var t = (b.p - a.p) ? (f - a.p) / (b.p - a.p) : 0;
      var lerp = function (k) { return a.c[k] + ((b.c[k] === undefined ? a.c[k] : b.c[k]) - a.c[k]) * t; };
      var alpha = (a.c[3] === undefined ? 1 : a.c[3]) + (((b.c[3] === undefined ? 1 : b.c[3])) - (a.c[3] === undefined ? 1 : a.c[3])) * t;
      // blend over the white page beneath the panel
      var mix = function (k) { return Math.round(lerp(k) * alpha + 255 * (1 - alpha)); };
      return 'rgb(' + mix(0) + ',' + mix(1) + ',' + mix(2) + ')';
    } catch (e) { return null; }
  }
  /* "Free shipping" tag on the one-time option, same look as the savings tags. Sits just left of the savings tag
     (2-pack / 3-pack) or on its own where the savings tag would be (single bag). */
  function paintShipBadge() {
    paintBadge(SHIPBADGE, '.rc-selection__onetime', 'Free shipping');
    var ship = document.getElementById(SHIPBADGE);
    var money = document.getElementById(BADGE);
    if (!ship) return;
    ship.style.minWidth = '';
    if (money) {
      var m = money.getBoundingClientRect();
      var an = anchorBox();
      ship.style.left = (an ? relLeft(m.left - 6, an) : Math.round(m.left + window.scrollX - 6)) + 'px';
      ship.style.top = (an ? relTop(m.top, an) : Math.round(m.top + window.scrollY)) + 'px';
    }
  }

  var touched = [];   // theme price nodes whose style we changed; reset on every render
  function render() {
    loadProduct();
    for (var t = 0; t < touched.length; t++) { touched[t].style.textDecoration = ''; touched[t].style.opacity = ''; touched[t].style.display = ''; }
    touched = [];
    var old = document.querySelectorAll('.' + TAG);
    for (var i = 0; i < old.length; i++) old[i].remove();

    var p = pack();
    if (!p) return;
    var pr = prices(p);
    var el = pr.el, list = pr.list;
    if (!el || !list) return;
    var holder = (pr.compareMode && el.closest && el.closest('.price__sale')) || el.parentElement || el;

    var subSel = subscribeSelected();
    var oneTotal = (pr.oneTime != null && pr.oneTime < list) ? pr.oneTime : null;   // null on a single bag
    var subTotal = (pr.sub != null && pr.sub < list) ? pr.sub : null;
    var total = subSel ? subTotal : oneTotal;

    if (total != null) {
      el.style.textDecoration = 'line-through';
      el.style.opacity = '0.6';
      touched.push(el);
      // New state: the theme already prints the variant price ($76) as its sale price. Hide it (style only) so the
      // one total printed below is the only live figure, whichever option is selected.
      if (pr.compareMode && pr.saleEl) { pr.saleEl.style.display = 'none'; touched.push(pr.saleEl); }
      var span = document.createElement('span');
      span.className = TAG;
      span.setAttribute('style', 'margin-left:8px;font-weight:700');
      var totalPart = document.createElement('span');
      totalPart.textContent = money(total);
      var savePart = document.createElement('span');
      savePart.setAttribute('style', 'color:rgb(var(--gg-edge, 15,134,106));margin-left:6px');  // founder 10-12: flavour border tone
      savePart.textContent = '(save ' + money(round2(list - total)) + ')';
      span.appendChild(totalPart);
      span.appendChild(savePart);
      holder.appendChild(span);
    }

    // One-time multipack saving: badge on the one-time option (dollars first, % beside it) against the list price
    // (the compare-at in the new state). Founder 2026-09-30: each option's tags show only while it is selected.
    paintBadge(BADGE, '.rc-selection__onetime', (oneTotal != null && !subSel) ? 'Save ' + money(round2(list - oneTotal)) + ' (' + pct(list - oneTotal, list) + '%)' : null);
    if (subSel) { var sb = document.getElementById(SHIPBADGE); if (sb) sb.remove(); } else { paintShipBadge(); }
    // Old state: Recharge prints the raw list ($120) on the one-time option -> covered with "$120 struck, $108".
    // New state: Recharge prints the variant price ($108); the same overlay puts the struck compare-at beside it.
    paintOneTimePrice(list, oneTotal);

    // Subscribe saving in dollars against list: $4 / $12 / $24 (vs $40 / $80 / $120) in both states.
    paintBadge(SUBBADGE, '.rc-purchase-option__subscription', (subTotal != null && subSel) ? 'Save ' + money(round2(list - subTotal)) + ' (' + pct(list - subTotal, list) + '%)' : null);
    if (subTotal != null) {
      var rs = document.documentElement.style;
      rs.setProperty('--gg-sub-save-label', JSON.stringify('Save ' + money(round2(list - subTotal))));  // founder 10-12: "Save $N", no percentage
      rs.setProperty('--gg-sub-save-hide', '0');
    }
    var theirs = findInWidget('.rc-purchase-option__subscription .rc-purchase-option__badge');
    if (theirs) theirs.style.visibility = subSel ? '' : 'hidden';
  }

  var pending = null;
  function schedule() { clearTimeout(pending); pending = setTimeout(render, 250); }
  loadProduct();

  // Founder 2026-10-12: Recharge's "Save N%" benefit line takes the same flavour tone as "(save $N)" next to the price.
  // Only a stylesheet is adopted into Recharge's benefits shadow root (no element is added, moved or edited), and it reads
  // --gg-edge, which inherits through the shadow boundary, so it recolours itself whenever the flavour changes.
  var benefitSheet = null;
  function tintBenefits() {
    try {
      var w = document.querySelector('recharge-subscription-widget');
      var b = w && w.shadowRoot && w.shadowRoot.querySelector('rc-benefits');
      var sr = b && b.shadowRoot;
      if (!sr || typeof CSSStyleSheet === 'undefined') return;
      if (!benefitSheet) {
        benefitSheet = new CSSStyleSheet();
        var fs = (sr.querySelector('li p') && getComputedStyle(sr.querySelector('li p')).fontSize) || '14px';
        benefitSheet.replaceSync('li:first-child p{color:rgb(var(--gg-edge, 18,18,18)) !important;font-weight:700 !important;font-size:var(--gg-sub-save-hide, ' + fs + ') !important}' +
          'li:first-child p::after{content:var(--gg-sub-save-label, "");font-size:' + fs + '}');
      }
      if (sr.adoptedStyleSheets.indexOf(benefitSheet) === -1) sr.adoptedStyleSheets = sr.adoptedStyleSheets.concat([benefitSheet]);
    } catch (e) {}
  }
  setInterval(tintBenefits, 1000);
  tintBenefits();

  // No scroll repositioning: badges live inside the form and scroll with it (re-measuring during iOS momentum scroll made them drift).
  window.addEventListener('resize', schedule);
  // Re-align when the widget changes size (an option expanding moves the boxes below it).
  (function watchWidget() {
    if (typeof ResizeObserver !== 'function') return;
    var host = document.querySelector('recharge-subscription-widget');
    if (!host) { setTimeout(watchWidget, 800); return; }
    new ResizeObserver(function () { schedule(); }).observe(host);
  })();

  function paintBadgeReposition() {
    var a = document.getElementById(BADGE);
    if (a) paintBadge(BADGE, '.rc-selection__onetime', a.textContent);
    if (document.getElementById(SHIPBADGE) && !subscribeSelected()) paintShipBadge();
    var b = document.getElementById(SUBBADGE);
    if (b) paintBadge(SUBBADGE, '.rc-purchase-option__subscription', b.textContent);
    schedule();   // the one-time price overlay is rebuilt from current values
  }

  document.addEventListener('change', schedule, true);
  document.addEventListener('click', schedule, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  [600, 1500, 2600, 4000, 6000].forEach(function (ms) { setTimeout(schedule, ms); });
})();
